import { ref } from 'vue';

/**
 * TODO: (welington) once we add support to TS in satellite, we should
 * drop the useEventBasedDialog from Helios and use this one instead.
 */

/**
 * Simple composable for managing modal state
 * @param {boolean} [initialValue=false] - Initial modal state
 * @returns {{isOpen: import('vue').Ref<boolean>, closeModal: () => void, openModal: () => void, toggleModal: () => void}}
 */
function useModal(initialValue = false) {
  const isOpen = ref(initialValue);

  const closeModal = () => (isOpen.value = false);
  const openModal = () => (isOpen.value = true);
  const toggleModal = () => (isOpen.value = !isOpen.value);

  return { isOpen, closeModal, openModal, toggleModal };
}

/**
 * Subscribe to a custom event
 * @template {Record<string, CustomEvent<any>>} TEventMap
 * @template {keyof TEventMap} K
 * @param {K} eventName - The name of the event
 * @param {EventListenerOrEventListenerObject} listener - The event listener
 * @param {EventTarget} [target=document] - The target to listen on
 */
export function subscribeToCustomEvent(eventName, listener, target = document) {
  target.addEventListener(eventName, listener);
}

/**
 * Unsubscribe from a custom event
 * @template {Record<string, CustomEvent<any>>} TEventMap
 * @template {keyof TEventMap} K
 * @param {K} eventName - The name of the event
 * @param {EventListenerOrEventListenerObject} listener - The event listener
 * @param {EventTarget} [target=document] - The target to unsubscribe from
 */
export function unsubscribeFromCustomEvent(eventName, listener, target = document) {
  target.removeEventListener(eventName, listener);
}

/**
 * Emit a custom event (overload without data)
 * @template {Record<string, CustomEvent<any>>} TEventMap
 * @template {keyof TEventMap} K
 * @param {K} eventName - The name of the event
 * @param {EventTarget} target - The target to emit the event on
 * @returns {void}
 */

/**
 * Emit a custom event (overload with data)
 * @template {Record<string, CustomEvent<any>>} TEventMap
 * @template {keyof TEventMap} K
 * @param {K} eventName - The name of the event
 * @param {TEventMap[K]['detail']} data - The event data
 * @param {EventTarget} [target] - The target to emit the event on
 * @returns {void}
 */

/**
 * Emit a custom event
 * @template {Record<string, CustomEvent<unknown>>} TEventMap
 * @template {keyof TEventMap} K
 * @param {K} eventName - The name of the event
 * @param {TEventMap[K]['detail']|EventTarget} [dataOrTarget] - The event data or target
 * @param {EventTarget} [maybeTarget] - Optional target if data is provided
 * @returns {void}
 */
export function emitCustomEvent(eventName, dataOrTarget, maybeTarget) {
  const target = maybeTarget ?? (dataOrTarget instanceof EventTarget ? dataOrTarget : document);
  const data = dataOrTarget instanceof EventTarget ? undefined : dataOrTarget;

  const event = new CustomEvent(eventName, { detail: data });
  target.dispatchEvent(event);
}

/**
 * Composable for listening to dialog events (used in dialog components)
 * @template {Record<string, CustomEvent<unknown>>} TEventMap
 * @template {keyof TEventMap} [K=keyof TEventMap]
 * @param {K} dialogName - The name of the dialog to listen to
 * @returns {{
 *   isDialogOpen: import('vue').Ref<boolean>,
 *   dialogProps: import('vue').Ref<TEventMap[K]['detail']|null>,
 *   closeDialog: () => void,
 *   getDialogProp: <TKey extends keyof TEventMap[K]['detail']>(key: TKey) => TEventMap[K]['detail'][TKey] | null,
 *   resetDialogProps: () => void,
 *   subscribeToDialogEvent: () => void,
 *   unsubscribeFromDialogEvent: () => void
 * }}
 */
export function useDialogListener(dialogName) {
  const { isOpen, openModal, closeModal } = useModal(false);
  const dialogProps = ref(null);

  const handleEvent = event => {
    dialogProps.value = event.detail;
    openModal();
  };

  const closeDialog = () => {
    if (!isOpen.value) {
      return;
    }

    closeModal();
  };

  /**
   * Type-safe access to dialog props
   * The return type is automatically inferred from the key based on the event type definition
   * @template {keyof TEventMap[K]['detail']} TKey
   * @param {TKey} key - The key of the property to get
   * @returns {TEventMap[K]['detail'][TKey] | null} The value of the property or null
   */
  const getDialogProp = key => {
    /** @type {TEventMap[K]['detail'] | null} */
    const props = dialogProps.value;
    return props?.[key] ?? null;
  };

  const resetDialogProps = () => {
    dialogProps.value = null;
  };

  return {
    isDialogOpen: isOpen,
    dialogProps,

    closeDialog,
    getDialogProp,
    resetDialogProps,
    subscribeToDialogEvent: () => subscribeToCustomEvent(dialogName, handleEvent),
    unsubscribeFromDialogEvent: () => unsubscribeFromCustomEvent(dialogName, handleEvent)
  };
}

/**
 * Composable for triggering dialog events (used in components that open dialogs)
 * @template {Record<string, CustomEvent<unknown>>} TEventMap
 * @template {keyof TEventMap} K
 * @param {K} dialogName - The name of the dialog to trigger
 * @returns {{
 *   triggerDialog: (newProps?: TEventMap[K]['detail']) => void
 * }}
 */
export function useDialogTrigger(dialogName) {
  /**
   * @param {*} [newProps] - Optional props to pass to the dialog
   */
  const triggerDialog = newProps => {
    emitCustomEvent(dialogName, newProps);
  };

  return {
    triggerDialog
  };
}
